<lib-modal
  data-testid="testprotokoll-dialog"
  [title]="'testResults.protocol.title' | translate"
  [subtitle]="subtitleKey() | translate: { run: runKey() }"
  [showClose]="true"
  [closeLabel]="'testResults.protocol.close' | translate"
  [showButtons]="false"
  [visible]="visible()"
  [contentTemplate]="content"
  [footerTemplate]="footer"
  (visibleChange)="visible.set($event)"
></lib-modal>

<ng-template #content>
  <div class="tp-body">
    <div class="tp-options">
      <div class="tp-label">{{ 'testResults.protocol.labels.always' | translate }}</div>
      <div class="tp-always">{{ 'testResults.protocol.alwaysText' | translate }}</div>

      <div class="tp-label">{{ 'testResults.protocol.labels.additional' | translate }}</div>
      <div class="tp-tiles">
        @for (key of sections; track key) {
          @let known = count(key) !== null;
          @let available = isAvailable(key);
          <button
            type="button"
            class="tp-tile"
            [class.tp-tile--on]="isOn(key)"
            [attr.data-testid]="'protokoll-option-' + key"
            [attr.aria-pressed]="isOn(key)"
            [disabled]="!available"
            (click)="toggle(key)"
          >
            <span class="tp-tile__box" aria-hidden="true">
              @if (isOn(key)) {
                ✓
              }
            </span>
            <span class="tp-tile__text">
              <span class="tp-tile__label">
                {{ 'testResults.protocol.options.' + key + '.label' | translate }}
                @if (available) {
                  <span class="tp-tile__count">{{ count(key) }}</span>
                }
              </span>
              <span class="tp-tile__hint">
                @if (known && !available) {
                  {{ 'testResults.protocol.notAvailable' | translate }}
                } @else {
                  {{ 'testResults.protocol.options.' + key + '.hint' | translate }}
                }
              </span>
            </span>
          </button>
        }
      </div>

      <div class="tp-label">{{ 'testResults.protocol.labels.steps' | translate }}</div>
      <lib-segmented-filter-control
        variant="radio"
        density="compact"
        [ariaLabel]="'testResults.protocol.labels.steps' | translate"
        [items]="[
          { id: 'all', label: 'testResults.protocol.all' | translate, count: stepCount() },
          { id: 'deviations', label: 'testResults.protocol.onlyDeviations' | translate, count: deviationCount() },
        ]"
        [value]="options().nurAbweichungen ? 'deviations' : 'all'"
        (valueChange)="showOnlyDeviations($event === 'deviations')"
      ></lib-segmented-filter-control>

      <div class="tp-label">{{ 'testResults.protocol.labels.file' | translate }}</div>
      <div class="tp-file">
        <span class="tp-file__badge" aria-hidden="true">PDF</span>
        <code class="tp-file__name" data-testid="protokoll-dateiname">{{ filename() }}</code>
      </div>

      @if (downloaded()) {
        <div class="tp-success" role="status" data-testid="protokoll-erfolg">
          {{ 'testResults.protocol.success' | translate }}
        </div>
      }
      @if (downloadFailed()) {
        <div class="tp-error" role="alert" data-testid="protokoll-download-fehler">
          {{ 'testResults.protocol.errors.download' | translate }}
        </div>
      }
    </div>

    <div class="tp-stage">
      @if (preview().loading) {
        <p class="tp-state" role="status" data-testid="protokoll-laedt">
          {{ 'testResults.protocol.loading' | translate }}
        </p>
      }
      @if (preview().failed) {
        <div class="tp-state tp-state--error" role="alert" data-testid="protokoll-fehler">
          <span>{{ 'testResults.protocol.errors.preview' | translate }}</span>
          <button type="button" lib-button variant="secondary" data-testid="protokoll-erneut" (click)="retry()">
            {{ 'testResults.protocol.retry' | translate }}
          </button>
        </div>
      } @else if (data(); as d) {
        <div class="tp-doc" data-testid="protokoll-vorschau">
          <div class="tp-doc__head">
            <img
              class="tp-doc__logo"
              src="assets/img/TestSolutions_Logo_white.png"
              [alt]="'testResults.protocol.doc.logoAlt' | translate"
            />
            <div class="tp-doc__head-text">
              <div class="tp-doc__title">{{ 'testResults.protocol.doc.title' | translate }}</div>
              <div class="tp-doc__sub">
                {{ d.kopf.laufKey }}
                @if (d.kopf.ausfuehrungNr; as nr) {
                  · {{ 'testResults.protocol.doc.execution' | translate: { nr: nr } }}
                }
                ·
                {{ 'testResults.protocol.doc.stand' | translate: { stand: stand() | date: 'dd.MM.yyyy, HH:mm' } }}
              </div>
            </div>
          </div>

          <dl class="tp-meta" data-testid="protokoll-meta">
            <dt>{{ 'testResults.protocol.meta.testfall' | translate }}</dt>
            <dd>{{ testfall(d) }}</dd>
            <dt>{{ 'testResults.protocol.meta.ausfuehrung' | translate }}</dt>
            <dd>{{ ausfuehrung(d) }}</dd>
            <dt>{{ 'testResults.protocol.meta.projekt' | translate }}</dt>
            <dd>{{ d.kopf.projekt }}</dd>
            <dt>{{ 'testResults.protocol.meta.umgebung' | translate }}</dt>
            <dd>{{ d.kopf.umgebung || '—' }}</dd>
            <dt>{{ 'testResults.protocol.meta.art' | translate }}</dt>
            <dd>{{ artUndQuelle(d) }}</dd>
            <dt>{{ 'testResults.protocol.meta.bearbeiter' | translate }}</dt>
            <dd>{{ d.kopf.bearbeiter || '—' }}</dd>
            <dt>{{ 'testResults.protocol.meta.durchgefuehrt' | translate }}</dt>
            <dd>{{ d.kopf.durchgefuehrtAm ? (d.kopf.durchgefuehrtAm | date: 'dd.MM.yyyy, HH:mm') : '—' }}</dd>
            <dt>{{ 'testResults.protocol.meta.dauer' | translate }}</dt>
            <dd>{{ duration(d.kopf.dauerMs) }}</dd>
          </dl>

          <div class="tp-band" [attr.data-status]="d.ergebnis.status" data-testid="protokoll-band">
            <app-res-pill
              size="sm"
              [status]="pill(d.ergebnis.status)"
              [label]="statusLabel(d.ergebnis.status) | translate"
            />
            <span class="tp-band__passed">
              {{
                'testResults.protocol.doc.passed'
                  | translate: { passed: d.ergebnis.bestanden, total: d.ergebnis.gesamt }
              }}
            </span>
            <span class="tp-band__counts">
              {{
                'testResults.protocol.doc.counts'
                  | translate
                    : {
                        failed: d.ergebnis.fehlgeschlagen,
                        blocked: d.ergebnis.blockiert,
                        skipped: d.ergebnis.uebersprungen,
                      }
              }}
            </span>
          </div>
          @if (d.ergebnis.fehlermeldung; as fehlermeldung) {
            <div class="tp-red" data-testid="protokoll-fehlermeldung">{{ fehlermeldung }}</div>
          }
          @for (bug of d.ergebnis.bugs; track bug.key) {
            <div class="tp-red tp-red--strong" data-testid="protokoll-fall-bug">
              {{ 'testResults.protocol.doc.bug' | translate: { key: bug.key } }}
            </div>
          }

          <div class="tp-section" data-testid="protokoll-abschnitt">
            {{
              (options().nurAbweichungen
                ? 'testResults.protocol.doc.stepsDeviations'
                : 'testResults.protocol.doc.steps'
              ) | translate
            }}
          </div>
          <div class="tp-steps tp-steps--head">
            <span>{{ 'testResults.protocol.columns.nr' | translate }}</span>
            <span>{{ 'testResults.protocol.columns.step' | translate }}</span>
            <span>{{ 'testResults.protocol.columns.duration' | translate }}</span>
            <span>{{ 'testResults.protocol.columns.result' | translate }}</span>
          </div>
          @for (schritt of steps(); track schritt.nr) {
            @if (schritt.newGroup) {
              <div class="tp-group" data-testid="protokoll-gruppe">{{ schritt.gruppe }}</div>
            }
            <div class="tp-steps" [class.tp-steps--deviation]="schritt.deviation" data-testid="protokoll-schritt">
              <span class="tp-muted">{{ schritt.nr }}</span>
              <span class="tp-step">
                <span class="tp-step__action">{{ schritt.aktion }}</span>
                @if (schritt.soll) {
                  <span class="tp-muted">
                    {{ 'testResults.protocol.doc.target' | translate: { text: schritt.soll } }}
                  </span>
                }
                @if (schritt.ist) {
                  <span [class]="schritt.deviation ? 'tp-red' : 'tp-muted'">
                    {{ 'testResults.protocol.doc.actual' | translate: { text: schritt.ist } }}
                  </span>
                }
                @if (schritt.kommentar) {
                  <span class="tp-muted tp-step__comment">
                    {{ 'testResults.protocol.doc.comment' | translate: { text: schritt.kommentar } }}
                  </span>
                }
                @for (bug of schritt.bugs; track bug.key) {
                  <span class="tp-red tp-red--strong">
                    {{ 'testResults.protocol.doc.bug' | translate: { key: bug.key } }}
                  </span>
                }
              </span>
              <span class="tp-muted">{{ duration(schritt.dauerMs) }}</span>
              <span>
                <app-res-pill
                  size="sm"
                  [status]="pill(schritt.status)"
                  [label]="statusLabel(schritt.status) | translate"
                />
              </span>
            </div>
          } @empty {
            <p class="tp-muted tp-empty">{{ 'testResults.protocol.doc.' + noStepsKey() | translate }}</p>
          }

          @if (d.testdaten?.length) {
            <div class="tp-section" data-testid="protokoll-abschnitt">
              {{ 'testResults.protocol.doc.testdaten' | translate }}
            </div>
            @for (blatt of d.testdaten; track $index) {
              <div class="tp-sheet" data-testid="protokoll-blatt">
                <strong class="tp-strong">
                  {{
                    blatt.aktuellerStand
                      ? ('testResults.protocol.doc.sheetCurrentState' | translate: { name: blatt.name })
                      : blatt.name
                  }}
                </strong>
                @if (blatt.zeilen.length) {
                  <div class="tp-sheet__scroll">
                    <table class="tp-sheet__table">
                      <thead>
                        <tr>
                          <th scope="col">{{ 'testResults.protocol.doc.row' | translate }}</th>
                          @for (spalte of blatt.spalten; track spalte) {
                            <th scope="col" class="tp-sheet__column">{{ spalte }}</th>
                          }
                        </tr>
                      </thead>
                      <tbody>
                        @for (zeile of blatt.zeilen; track $index) {
                          <tr>
                            <td>#{{ $index + 1 }}</td>
                            @for (spalte of blatt.spalten; track spalte) {
                              <td>{{ zeile[spalte] }}</td>
                            }
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                } @else {
                  <p class="tp-muted tp-empty">{{ 'testResults.protocol.doc.noRows' | translate }}</p>
                }
              </div>
            }
          }

          @if (d.kommentare?.length) {
            <div class="tp-section" data-testid="protokoll-abschnitt">
              {{ 'testResults.protocol.doc.kommentare' | translate }}
            </div>
            @for (kommentar of d.kommentare; track $index) {
              <div class="tp-comment" data-testid="protokoll-kommentar">
                <span class="tp-muted">{{ kommentar.zeit | date: 'dd.MM.yyyy, HH:mm' }}</span>
                <span class="tp-step">
                  <strong class="tp-strong">{{ commentAuthor(kommentar) }}</strong>
                  @if (kommentar.text) {
                    <span>{{ kommentar.text }}</span>
                  }
                  @if (kommentar.dateien.length) {
                    <span class="tp-muted">
                      {{ 'testResults.protocol.doc.files' | translate: { files: kommentar.dateien.join(', ') } }}
                    </span>
                  }
                </span>
              </div>
            }
          }

          @if (d.anhaenge?.length) {
            <div class="tp-section" data-testid="protokoll-abschnitt">
              {{ 'testResults.protocol.doc.anhaenge' | translate }}
            </div>
            @for (anhang of d.anhaenge; track $index) {
              <div data-testid="protokoll-anhang">{{ attachmentLine(anhang, $index) }}</div>
            }
          }

          <div class="tp-doc__foot" data-testid="protokoll-fuss">
            <span>
              {{
                (closed() ? 'testResults.protocol.doc.footerClosed' : 'testResults.protocol.doc.footerOpen')
                  | translate: { stand: stand() | date: 'dd.MM.yyyy, HH:mm' }
              }}
            </span>
            <span>{{ 'testResults.protocol.doc.page' | translate }}</span>
          </div>
        </div>
      }
    </div>
  </div>
</ng-template>

<ng-template #footer>
  <div class="tp-actions">
    <button type="button" lib-button variant="secondary" data-testid="protokoll-schliessen" (click)="close()">
      {{ 'testResults.protocol.close' | translate }}
    </button>
    <button
      type="button"
      lib-button
      variant="primary"
      data-testid="testprotokoll-download"
      [disabled]="!canDownload()"
      (click)="download()"
    >
      @if (downloading()) {
        {{ 'testResults.protocol.downloading' | translate }}
      } @else {
        <span aria-hidden="true">↓</span>
        {{ 'testResults.protocol.download' | translate }}
      }
    </button>
  </div>
</ng-template>
